import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label } from "reactstrap";
import Select from "react-select";
import PropTypes from "prop-types";
import companyService from "services/CompanyService";
import OrganizationalStructureService from "services/OrganizationalStructureService";
import { Copy, X } from "@phosphor-icons/react";

const CopyActionPlanModal = ({ isOpen, toggle, onCopy, actionPlanData }) => {
	// Estilos customizados para os selects
	const customStyles = {
		option: (provided, state) => ({
			...provided,
			color: state.isSelected ? "blue" : "black",
			backgroundColor: "white",
			"&:hover": {
				backgroundColor: "#556ee6",
			},
		}),
		menu: (provided) => ({
			...provided,
			zIndex: 9999,
		}),
		menuPortal: (provided) => ({
			...provided,
			zIndex: 9999,
		}),
	};
	const [companies, setCompanies] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [selectedCompany, setSelectedCompany] = useState(null);
	const [selectedDepartment, setSelectedDepartment] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	// Buscar todas as empresas
	const getCompanies = async () => {
		try {
			const response = await companyService.get();
			const companiesOptions = response.map((company) => ({
				value: company.id,
				label: company.name,
			}));
			setCompanies(companiesOptions);
		} catch (error) {
			console.log(error);
		}
	};

	// Buscar departamentos da empresa selecionada
	const getDepartments = async (companyId) => {
		if (!companyId) {
			setDepartments([]);
			return;
		}

		try {
			const response = await OrganizationalStructureService.getByCompanySchema(companyId);
			const companyDepartments = response.filter(
				(dept) => dept.company_id === companyId
			);
			
			const departmentsOptions = companyDepartments.map((dept) => ({
				value: dept.id,
				label: dept.name,
			}));
			setDepartments(departmentsOptions);
		} catch (error) {
			console.log("Erro ao buscar departamentos:", error);
		}
	};

	// Quando uma empresa é selecionada, buscar seus departamentos
	useEffect(() => {
		if (selectedCompany) {
			getDepartments(selectedCompany.value);
			setSelectedDepartment(null); // Resetar departamento selecionado
		} else {
			setDepartments([]);
			setSelectedDepartment(null);
		}
	}, [selectedCompany]);

	// Carregar empresas quando o modal abrir
	useEffect(() => {
		if (isOpen) {
			getCompanies();
		}
	}, [isOpen]);

	const handleCopy = () => {
		if (selectedCompany && selectedDepartment) {
			onCopy({
				company_id: selectedCompany.value,
				department_id: selectedDepartment.value,
				company_name: selectedCompany.label,
				department_name: selectedDepartment.label,
				actionPlanData: actionPlanData,
			});
			handleClose();
		}
	};

	const handleClose = () => {
		setSelectedCompany(null);
		setSelectedDepartment(null);
		setDepartments([]);
		toggle();
	};

	const isFormValid = selectedCompany && selectedDepartment;

	return (
		<Modal 
			isOpen={isOpen} 
			toggle={handleClose} 
			size="md" 
			centered
			style={{ zIndex: 9998 }}
		>
			<ModalHeader toggle={handleClose}>
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<Copy size={20} />
					Copiar Plano de Ação
				</div>
			</ModalHeader>
			<ModalBody>
				<div style={{ marginBottom: 20 }}>
					<p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
						Para qual empresa você deseja copiar este plano de ação?
					</p>
				</div>

				<FormGroup className="mb-3">
					<Label>
						Empresa <span className="text-danger">*</span>
					</Label>
					<Select
						placeholder="Selecione uma empresa"
						options={companies}
						value={selectedCompany}
						onChange={setSelectedCompany}
						isClearable
						isSearchable
						styles={customStyles}
						menuPortalTarget={document.body}
						menuPosition="fixed"
					/>
				</FormGroup>

				<FormGroup className="mb-3">
					<Label>
						Departamento <span className="text-danger">*</span>
					</Label>
					<Select
						placeholder="Selecione um departamento"
						options={departments}
						value={selectedDepartment}
						onChange={setSelectedDepartment}
						isDisabled={!selectedCompany}
						isClearable
						isSearchable
						styles={customStyles}
						menuPortalTarget={document.body}
						menuPosition="fixed"
					/>
					{!selectedCompany && (
						<small className="text-muted">
							Selecione uma empresa primeiro para ver os departamentos disponíveis
						</small>
					)}
				</FormGroup>
			</ModalBody>
			<ModalFooter>
				<Button
					onClick={handleClose}
					style={{
						background: "none",
						padding: 10,
						cursor: "pointer",
						color: "black",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 2,
					}}
				>
					<X size={16} /> Cancelar
				</Button>
				<Button
					onClick={handleCopy}
					disabled={!isFormValid}
					style={{
						width: 150,
						background: "none",
						padding: 10,
						cursor: isFormValid ? "pointer" : "not-allowed",
						color: isFormValid ? "black" : "#ccc",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 2,
						opacity: isFormValid ? 1 : 0.6,
					}}
				>
					<Copy size={16} /> Copiar
				</Button>
			</ModalFooter>
		</Modal>
	);
};

CopyActionPlanModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	toggle: PropTypes.func.isRequired,
	onCopy: PropTypes.func.isRequired,
	actionPlanData: PropTypes.object,
};

export default CopyActionPlanModal;
